import type { ContestManageStore } from '../context/types/store';
import type { FormattedErrors } from '../context/utils/formatZodErrors';

/**
 * Checks if any sections are currently loading
 */
export function isSectionLoading(store: ContestManageStore): boolean {
  return Object.values(store.sectionsCache).some((section) => section?.isLoading === true);
}

/**
 * Checks if any fields are currently loading
 */
export function isFieldLoading(store: ContestManageStore): boolean {
  return Object.values(store.fieldsCache).some((field) => field?.isLoading === true);
}

/**
 * Checks if any sections or fields are currently loading
 */
export function isAnythingLoading(store: ContestManageStore): boolean {
  return isSectionLoading(store) || isFieldLoading(store);
}

/**
 * Recursively checks if there are any validation errors in the errors object
 */
function hasNestedErrors(errors: FormattedErrors): boolean {
  return Object.values(errors).some((value) => {
    if (Array.isArray(value)) {
      return value.length > 0;
    }
    if (typeof value === 'object' && value !== null) {
      return hasNestedErrors(value as FormattedErrors);
    }
    return false;
  });
}

/**
 * Checks if there are any validation errors
 */
export function hasValidationErrors(errors: FormattedErrors | null): boolean {
  if (!errors) {
    return false;
  }
  return hasNestedErrors(errors);
}

/**
 * Checks if there are validation errors for a specific field path or any of its nested children
 * @param errors - The formatted errors object
 * @param sectionKey - The section key (e.g., 'entries')
 * @param fieldPath - The field path within the section (e.g., ['contestSizeSettings'])
 * @returns boolean indicating if there are errors at this path or any nested paths
 */
export function hasErrorsInPath(
  errors: FormattedErrors | null,
  sectionKey: string,
  fieldPath: string[] = []
): boolean {
  if (!errors) {
    return false;
  }

  // Since the form structure uses 'form' as the main section key,
  // we need to check errors under the 'form' key and then navigate to the field path
  let targetErrors: FormattedErrors | string[] | undefined;

  if (sectionKey === 'form' && errors.form) {
    // For form section, start from the form errors
    targetErrors = errors.form as FormattedErrors;
  } else if (errors[sectionKey]) {
    // Direct section access
    targetErrors = errors[sectionKey] as FormattedErrors;
  } else if (errors.form) {
    // Try under form key
    targetErrors = (errors.form as FormattedErrors)[sectionKey] as FormattedErrors | undefined;
  }

  if (!targetErrors) {
    return false;
  }

  // If no field path provided, check if section has any errors
  if (fieldPath.length === 0) {
    return hasNestedErrors(targetErrors as FormattedErrors);
  }

  // Navigate through the field path
  let current: FormattedErrors | string[] | undefined = targetErrors;

  for (const pathSegment of fieldPath) {
    if (!current || Array.isArray(current)) {
      return false;
    }
    current = current[pathSegment] as FormattedErrors | string[] | undefined;
  }

  // If we found something at this path, check if it has errors
  if (!current) {
    return false;
  }

  if (Array.isArray(current)) {
    return current.length > 0;
  }

  return hasNestedErrors(current as FormattedErrors);
}
